import React, {useEffect, useContext} from 'react'
import ReactModal from 'react-modal'
import {default as range} from "lodash/range"
import {ConsumerModal} from 'js/studios/shared/modal'
import {classNames, keyActivationHandler} from 'js/studios/shared/utils'
import {trackEventSP} from 'js/studios/shared/tracking'
import {FlowFramesCtx} from "js/studios/shared/unredux/contexts/flow"
import {useOnMount} from "js/studios/shared/unredux/hooks"
import LogoWordmarkSvg from "images/brand2025/logo-wordmark.svg"
import InfoSvg from "images/brand2025/icons/info.svg"

export const BUDGET_OPTIONS = range(100, 2500, 100).concat(range(2500, 10000, 500)).concat(range(10000, 25000, 1000))

export const COMMON_GEM_SHAPES = [
  {
    name: "round",
    img: `${window.cm.structureUrl}/static/images/brand2025/gem-shapes/round.png`,
    label: "Round"
  },
  {
    name: "princess",
    img: `${window.cm.structureUrl}/static/images/brand2025/gem-shapes/princess.png`,
    label: "Princess"
  },
  {
    name: "oval",
    img: `${window.cm.structureUrl}/static/images/brand2025/gem-shapes/oval.png`,
    label: "Oval"
  },
  {
    name: "emerald",
    img: `${window.cm.structureUrl}/static/images/brand2025/gem-shapes/emerald.png`,
    label: "Emerald"
  },
  {
    name: "pear",
    img: `${window.cm.structureUrl}/static/images/brand2025/gem-shapes/pear.png`,
    label: "Pear"
  },
  {
    name: "elongated_cushion",
    img: `${window.cm.structureUrl}/static/images/brand2025/gem-shapes/elongated-cushion.png`,
    label: "Elongated Cushion"
  },
  {
    name: "cushion",
    img: `${window.cm.structureUrl}/static/images/brand2025/gem-shapes/square-cushion.png`,
    label: "Square Cushion"
  },
  {
    name: "marquise",
    img: `${window.cm.structureUrl}/static/images/brand2025/gem-shapes/marquise.png`,
    label: "Marquise"
  },
  {
    name: "heart",
    img: `${window.cm.structureUrl}/static/images/brand2025/gem-shapes/heart.png`,
    label: "Heart"
  },
  {
    name: "radiant",
    img: `${window.cm.structureUrl}/static/images/brand2025/gem-shapes/radiant.png`,
    label: "Radiant"
  },
  {
    name: "asscher",
    img: `${window.cm.structureUrl}/static/images/brand2025/gem-shapes/asscher.png`,
    label: "Asscher"
  }

]
export const UNCOMMON_GEM_SHAPES = [
  {
    name: "rose",
    img: `${window.cm.structureUrl}/static/images/brand2025/gem-shapes/rose.png`,
    label: "Rose"
  },
  {
    name: "kite",
    img: `${window.cm.structureUrl}/static/images/brand2025/gem-shapes/kite.png`,
    label: "Kite"
  },
  {
    name: "hexagon",
    img: `${window.cm.structureUrl}/static/images/brand2025/gem-shapes/hexagon.png`,
    label: "Hexagon"
  },
  {
    name: "trillion",
    img: `${window.cm.structureUrl}/static/images/brand2025/gem-shapes/trillion.png`,
    label: "Trillion"
  },
  {
    name: "calf_coffin",
    img: `${window.cm.structureUrl}/static/images/brand2025/gem-shapes/calf-coffin.png`,
    label: "Calf / Coffin"
  },
  {
    name: "old_mine",
    img: `${window.cm.structureUrl}/static/images/brand2025/gem-shapes/old-mine.png`,
    label: "Old Mine"
  },
  {
    name: "old_euro",
    img: `${window.cm.structureUrl}/static/images/brand2025/gem-shapes/old-euro.png`,
    label: "Old Euro"
  },
  {
    name: "half_moon",
    img: `${window.cm.structureUrl}/static/images/brand2025/gem-shapes/half-moon.png`,
    label: "Half Moon"
  },
  {
    name: "shield",
    img: `${window.cm.structureUrl}/static/images/brand2025/gem-shapes/shield.png`,
    label: "Shield"
  },
  {
    name: "other",
    img: `${window.cm.structureUrl}/static/images/brand2025/gem-shapes/other.png`,
    label: "Other"
  }
]

export function NextBtn({children}) {
  return <div className="btn-group anim--fade-in mt--lg">
    <button className="btn btn--lg btn--wide btn--arrow-right">{children || "Next"}</button>
  </div>
}

export const FrameHeader = ({children, h2ClassName}) => {
  return <div className="flow-frame__header">
    <PreviousLink />
    <h2 className={classNames("txt--center", h2ClassName)}>{children}</h2>
  </div>
}

export const Frame = ({dark=false, offWhite=false, cta="Next", canSubmit, onSubmit, className, children}) => {
  // for iOS, overriding --page-bg to match this frame's bg gets Safari status/address bars to match
  useEffect(() => {
    if (dark) {
      document.body.style.setProperty("--page-bg", "var(--burgundy)")
    } else if (offWhite) {
      document.body.style.setProperty("--page-bg", "var(--off-white)")
    }
    if (dark || offWhite) {
      return () => document.body.style.removeProperty("--page-bg")
    }
  }, [dark, offWhite])

  return <form
    className={classNames("flow-frame anim--fade-in", {"flow-frame--dark": dark, "flow-frame--off-white": offWhite}, className)}
    onSubmit={onSubmit}
    >
    <div className="flow-frame__body">
      {children}
      {canSubmit && cta !== false && <NextBtn>{cta || "Next"}</NextBtn>}
    </div>
  </form>
}


export function PreviousLink({onClick}) {
  const {gotoPrevFrame} = useContext(FlowFramesCtx) || {}

  if (!onClick) {
    onClick = gotoPrevFrame
  }

  const innerOnClick = ev => {
    ev.preventDefault()
    onClick(ev)
  }

  return <a
    className="previous"
    href="#"
    tabIndex="0"
    aria-label="Go back"
    onClick={innerOnClick}>
    <span className="icon icon--arrow-left"></span>
  </a>
}

// display a choice for an onboarding question with several varieties
export function CheckMarkCard({disabled, className, checked, onClick, imgs, value, label, description, children}) {
  const innerOnClick = (ev) => {
    ev.preventDefault()
    if (disabled) { return }
    if (onClick) {
      onClick({preventDefault: () => {}, target: {checked: !checked, value: value}})
    }
  }

  return <div
    className={classNames("check-mark-card", className)}
    role="button"
    aria-pressed={checked ? "true" : "false"}
    tabIndex="0"
    onClick={innerOnClick}
    onKeyDown={keyActivationHandler(innerOnClick)}
    data-disabled={disabled || undefined}
    >
    {imgs && <div className="check-mark-card__imgs">
      {imgs.map(img => <img key={img} src={img} role="presentation" alt="" />)}
    </div>}
    {label && <div className="check-mark-card__label"><b>{label}</b></div>}
    {description && <div className="check-mark-card__description txt--body-2">{description}</div>}
    {children ? <div className="check-mark-card__extra">{children}</div> : null}
    <div className="icon icon--check icon--matte"></div>
  </div>
}

export class HelpModal extends React.Component {
  componentDidUpdate(prevProps, _prevState) {
    if (prevProps.isOpen !== this.props.isOpen) {
      const action = this.props.isOpen ? 'action_open_help' : 'action_close_help'
      trackEventSP(action, 'consultation', {name: this.props.name})
    }
  }

  componentDidMount() {
    ReactModal.setAppElement('body')
  }

  handleClose = ev => {
    ev.preventDefault()
    ev.stopPropagation()
    this.props.onClose(ev)
  }

  render() {
    return <ConsumerModal
      isOpen={this.props.isOpen}
      className="ReactModal__content--padded"
      onRequestClose={this.props.onClose}
      contentLabel="help-modal"
      >
      {this.props.children}
    </ConsumerModal>
  }
}

export function HelpLink({label, onClick}) {
  const innerOnClick = ev => {
    ev.preventDefault()
    ev.stopPropagation()
    onClick(ev)
  }

  return <a
    className="help" aria-label={label || "Show help"} tabIndex="0" href="#"
    onClick={innerOnClick}>
    <InfoSvg />
  </a>
}

export function Splash({className, splashClassName, children}) {
  // for iOS, tweaking --page-bg to burgundy looks nicer as it gets status/address bars to match page
  useOnMount(() => {
    document.body.style.setProperty("--page-bg", "var(--burgundy)")
    return () => document.body.style.removeProperty("--page-bg")
  })

  return <div className={classNames("splash", className)}>
    <div className="splash__main anim--fade-in"><div className="splash__content">{children}</div></div>
    <div className="splash__logo anim--fade-in">
      <a href="/" aria-label="CustomMade homepage"><LogoWordmarkSvg /></a>
    </div>
    <div className={classNames("splash__hero anim--fade-in", splashClassName)}></div>
  </div>
}
